/**
 * CvShareSheet (web): the "Share your Tabbio" experience as a single
 * multi-view WebDialog. The main view mirrors native: hero card (its CTA is
 * live: "View CV" opens the CV, "Download CV" exports the PDF), one link pill
 * with copy + share, and a row list (Share with contact info · QR code · Add
 * to LinkedIn · Download CV · Privacy controls). Every row's view is reached
 * in-dialog with a back button. The `customize` view pins the dialog header
 * and the card and scrolls only its form (`scrollable={false}` + `contentFill`).
 */
import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { LockIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { LinkedInIcon } from '../icons/LinkedInIcon';
import { TABBIO_MARK_PATH } from '../common/TabbioMark';
import { useRouter } from 'expo-router';
import { QRCodeCanvas } from 'qrcode.react';
import {
  CircleCheck,
  FileDown,
  QrCode,
  ShieldCheck,
} from 'lucide-react';
import { FilePhoneIcon } from '../icons/FilePhoneIcon.web';
import { useTranslation } from 'react-i18next';
import { defaultShareCardConfig } from '@tabbio/utils/cv-share-card';
import { splitCvHandle } from '@tabbio/utils/public-url';
import {
  withCvShareRef,
  withOwnerViewMarker,
  type CvShareChannel,
} from '@tabbio/utils/analytics/cv-traffic';
import { colors } from '@tabbio/tokens';
import type { ExportFormat } from '../../lib/cv-export';
import { trackProductEvent } from '../../lib/product-analytics';
import type { CvVisibilityOptions } from './CVSharePanel';
import type { ProfileVisibility } from '@tabbio/types';
import { useCVData } from '../../lib/cv-mock-data';
import { useCanDownloadCvTarget } from '../../hooks/useCanDownloadCvTarget';
import { useCvExportEntitlement } from '../../hooks/useCvExportEntitlement';
import { useEntitlement } from '../../providers/SubscriptionProvider.shared';
import { useUpgradeModal } from '../../providers/UpgradeModalProvider';
import { getCvShareVisibilityDefaults } from '../../lib/cv-privacy';
import { openMarketingLink } from '../../lib/marketing-links';
import { useLocale } from '../../hooks/useLocale';
import { useToast } from '../../hooks/useToast';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { ProIcon } from '../icons/ProIcon';
import { WebDialog } from '../common/WebDialog.web';
import { ShareCardPreview } from './ShareCardPreview.web';
import { CustomizeCardSheet } from './CustomizeCardSheet.web';
import { CvPeopleManager } from './CvPeopleManager.web';
import { useCompactCvShareLink } from './useCompactCvShareLink';
import { ShareLinkDetails } from './ShareLinkDetails';
import { shareLinkCvIdentity } from '../../lib/share-link-receipt';
import { useTailoredCVs } from '../../hooks/useTailoredCvApi';
import { resolveLinkedInProfileUrl } from '../../lib/linkedin-profile-url';
import { writeToClipboard } from '../../lib/clipboard.web';
import {
  getLinkInBioPlatform,
  isLinkInBioPlatformId,
  type LinkInBioPlatform,
  type LinkInBioPlatformId,
} from '../../lib/link-in-bio';
import { AddToBioGuide } from '../profile/AddToBioGuide.web';

const DOWNLOAD_FORMATS: ExportFormat[] = ['pdf', 'docx', 'txt', 'md'];

/** `bio:<platform>` keeps the add-to-bio views in the same flat view union. */
const BIO_PREFIX = 'bio:';

type FixedView = 'main' | 'contact' | 'qr' | 'download' | 'customize' | 'access';
type View = FixedView | `bio:${string}`;
type ShareLinkKind = 'public' | 'private';

export interface CvShareSheetProps {
  publicUrl?: string | null;
  onDownload: (
    format: ExportFormat,
    options?: { visibility?: CvVisibilityOptions; variantId?: string | null },
  ) => Promise<void>;
  onClose?: () => void;
}

export function CvShareSheet({ publicUrl, onDownload, onClose }: CvShareSheetProps) {
  const { t } = useTranslation('seeker');
  // The add-to-bio walkthroughs are shared with the company share sheet, so
  // their copy lives in `common`.
  const { t: tCommon } = useTranslation('common');
  const router = useRouter();
  const { profile, cvConfig, updateCVConfig, target, privacy } = useCVData();
  // A grantee without the export permission gets no Download controls: the
  // API answers those exports with NOT_FOUND (audit A03/A07).
  const canDownload = useCanDownloadCvTarget(target);
  const exportAccess = useCvExportEntitlement();
  const canBrand = useEntitlement('individual.custom_branding').allowed;
  const upgrade = useUpgradeModal();
  const baseVisibility = useMemo(
    () => getCvShareVisibilityDefaults(privacy),
    [privacy],
  );

  const toast = useToast();
  const { locale, isRTL } = useLocale();
  const [view, setView] = useState<View>('main');
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<ShareLinkKind | null>(null);
  // Phone & email in the downloaded FILE only. The contact link always
  // carries them; this switch never touches it.
  const [includeContact, setIncludeContact] = useState(
    baseVisibility.showEmail || baseVisibility.showPhone,
  );
  const [revoking, setRevoking] = useState(false);
  // Why the Access view was opened: a platform id when a share action hit the
  // public-CV gate (its notice names that platform), `'link'` when the lock
  // notice or a link action did, or null when the user opened Access directly.
  const [accessReason, setAccessReason] = useState<
    LinkInBioPlatformId | 'link' | null
  >(null);
  const qrWrapRef = useRef<HTMLDivElement>(null);

  const visibilityForDownload = useMemo<CvVisibilityOptions>(
    () => ({
      ...baseVisibility,
      showEmail: includeContact,
      showPhone: includeContact,
    }),
    [baseVisibility, includeContact],
  );
  // "CV with contact info" is exactly that: phone & email always on.
  const contactLinkVisibility = useMemo<CvVisibilityOptions>(
    () => ({ ...baseVisibility, showEmail: true, showPhone: true }),
    [baseVisibility],
  );
  // A public link that actually resolves. While the profile is private the
  // public URL exists but 404s, so it does not count as a way to share.
  const hasWorkingPublicLink =
    Boolean(publicUrl) && privacy.profileVisibility === 'public';
  const {
    publicUrl: publicShareUrl,
    privateUrl,
    privateLink,
    visibilitySyncError,
    revoked: contactLinkRevoked,
    isCreatingPrivateUrl,
    isLoadingExistingLink,
    isSyncingPrivateVisibility,
    createPrivateUrl,
    revokePrivateUrl,
    retryPrivateVisibility,
  } = useCompactCvShareLink({
    target: target ?? 'master',
    publicUrl,
    visibility: contactLinkVisibility,
    // Never minted in the background: the owner creates it from its view.
    requiresPrivateLink: false,
    // ...but one made earlier (and not revoked) is shown again on open.
    adoptExistingContactLink: true,
  });
  // Which CV the contact link shares (audit E03): see CvShareSheet.tsx.
  const shareTarget = target ?? 'master';
  const ownCvs = useTailoredCVs(
    view === 'contact' &&
      shareTarget !== 'master' &&
      !shareTarget.includes(':'),
  );
  const linkCv = shareLinkCvIdentity(shareTarget, ownCvs.data);
  const card = cvConfig.card ?? defaultShareCardConfig({ showPhoto: true });
  // See CvShareSheet.tsx: the entitlement is re-checked so a lapsed subscriber's
  // stale `hideBranding: true` cannot strip the mark from a free QR.
  const hideQrLogo = canBrand && card.hideBranding;
  const { handle: publicHandle, suffix: publicDotcom } = splitCvHandle(publicShareUrl);
  const { handle: privateHandle, suffix: privateDotcom } = splitCvHandle(privateUrl);
  const linkedinProfileUrl = resolveLinkedInProfileUrl(profile.linkedin);
  const close = onClose ?? (() => router.back());

  // Every URL handed out carries `?ref=<channel>` so the owner's analytics can
  // name where a visit came from; the URL shown on screen stays clean.
  const copyUrl = async (
    url: string | null | undefined,
    kind: ShareLinkKind,
    channel: CvShareChannel | LinkInBioPlatformId = 'copy',
  ) => {
    if (!url) return;
    // `navigator.clipboard` is absent in an insecure context and rejects on a
    // denied permission, so a bare call threw an unhandled rejection and left
    // the sheet silent. Never show the copied state for a copy that failed.
    if (!(await writeToClipboard(withCvShareRef(url, channel)))) {
      setDownloadError(tCommon('publicProfile.shareSheet.copyFailedHint'));
      return;
    }
    setDownloadError(null);
    setCopiedLink(kind);
    setTimeout(() => setCopiedLink(null), 1600);
    trackProductEvent('cv_shared', { channel: 'copy_link', kind });
  };

  const nativeShareUrl = async (
    url: string | null | undefined,
    kind: ShareLinkKind = 'public',
  ) => {
    if (!url) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ url: withCvShareRef(url, 'share') });
        trackProductEvent('cv_shared', { channel: 'native_share' });
        return;
      } catch {
        /* cancelled */
      }
    }
    void copyUrl(url, kind);
  };

  /**
   * The public URL 404s while the profile is not public. Every action that
   * hands it out (open, QR, bio guides) stops here and opens Access instead.
   */
  const requirePublicLink = () => {
    if (!publicShareUrl) return false;
    if (!hasWorkingPublicLink) {
      setAccessReason('link');
      setView('access');
      return false;
    }
    return true;
  };

  const openQr = () => {
    if (!requirePublicLink()) return;
    setView('qr');
    trackProductEvent('cv_shared', { channel: 'qr' });
  };

  const openCv = () => {
    if (!requirePublicLink()) return;
    // The owner marker keeps the owner's own visit out of their view count.
    if (publicShareUrl) {
      window.open(withOwnerViewMarker(publicShareUrl), '_blank', 'noopener,noreferrer');
    }
  };

  const createContactLink = () => {
    void createPrivateUrl()
      .then(() => {
        toast.show({ title: t('cv.share.contact.linkReady'), type: 'success' });
        trackProductEvent('cv_shared', { channel: 'contact_link_created' });
      })
      .catch(() => {
        toast.show({ title: t('cv.share.linkCreateFailed'), type: 'error' });
      });
  };

  const revokeContactLink = () => {
    setRevoking(true);
    void revokePrivateUrl()
      .then(() => {
        toast.show({ title: t('cv.share.contact.linkRevoked'), type: 'success' });
      })
      .catch(() => {
        toast.show({ title: t('cv.share.contact.revokeFailed'), type: 'error' });
      })
      .finally(() => setRevoking(false));
  };

  const selectSharePlatform = (platform: LinkInBioPlatform) => {
    if (!publicShareUrl) return;
    // A bio link pointing at a private CV is worse than no link, so the gate
    // covers every platform — and its notice names the one that was picked.
    if (privacy.profileVisibility !== 'public') {
      setAccessReason(platform.id);
      setView('access');
      return;
    }
    // A platform with no bio walkthrough (Telegram) would open a view with
    // zero steps, so its tile composes a post straight away instead. Mirrors
    // ProfileShareSheet.
    if (!platform.stepsKey) {
      const intent = platform.shareIntent?.(
        encodeURIComponent(withCvShareRef(publicShareUrl, platform.id)),
      );
      if (intent) {
        window.open(intent, '_blank', 'noopener,noreferrer');
        trackProductEvent('cv_shared', { channel: platform.id });
      }
      return;
    }
    setView(`${BIO_PREFIX}${platform.id}`);
    // A guide is instructions, not a share: counted apart so `cv_shared`
    // stays a share.
    trackProductEvent('cv_share_guide_opened', { platform: platform.id });
  };

  const openBioGuide = (id: LinkInBioPlatformId) => {
    const platform = getLinkInBioPlatform(id);
    if (platform) selectSharePlatform(platform);
  };

  const download = async (next: ExportFormat) => {
    if (exportAccess.isFormatLocked(next)) return exportAccess.openUpgrade();
    setFormat(next);
    setDownloading(true);
    setDownloadError(null);
    try {
      await onDownload(next, { visibility: visibilityForDownload });
      setView('main');
    } catch (error) {
      setDownloadError(
        error instanceof Error
          ? error.message
          : t('cv.share.downloadFailed'),
      );
    } finally {
      setDownloading(false);
    }
  };

  const saveQr = () => {
    const canvas = qrWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'tabbio-cv-qr.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
    trackProductEvent('cv_shared', { channel: 'qr_download' });
  };

  const bioPlatform = (() => {
    if (!view.startsWith(BIO_PREFIX)) return null;
    const id = view.slice(BIO_PREFIX.length);
    return isLinkInBioPlatformId(id) ? (getLinkInBioPlatform(id) ?? null) : null;
  })();

  const fixedTitles: Record<FixedView, string> = {
    main: t('cv.share.title'),
    contact: t('cv.share.contact.title'),
    qr: t('cv.share.qrTitle'),
    download: t('cv.share.downloadCv'),
    customize: t('cv.customizeCard.editorTitle'),
    access: t('cv.share.accessTitle'),
  };
  const fixedSubtitles: Partial<Record<FixedView, string>> = {
    qr: t('cv.share.qrSubtitle'),
    access: t('cv.share.accessSubtitle'),
  };
  const dialogTitle = bioPlatform
    ? tCommon(bioPlatform.labelKey)
    : fixedTitles[view as FixedView];
  const dialogSubtitle = bioPlatform
    ? tCommon('linkInBio.guideSubtitle')
    : fixedSubtitles[view as FixedView];
  const chevronName = isRTL ? 'chevron_left' : 'chevron_right';

  return (
    <WebDialog
      title={dialogTitle}
      subtitle={dialogSubtitle}
      onClose={close}
      onBack={view !== 'main' ? () => setView('main') : close}
      maxWidth={520}
      // The editor pins the card and scrolls its own form.
      scrollable={view !== 'customize'}
      contentFill={view === 'customize'}
    >
      {view === 'main' ? (
        <div style={stack}>
          <ShareCardPreview
            publicUrl={publicShareUrl}
            onCustomizePress={() => setView('customize')}
            onViewPress={openCv}
            canDownload={canDownload}
            onDownloadPress={() => {
              if (downloading) return;
              void download('pdf');
            }}
          />

          {publicShareUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <LinkPill
                handle={publicHandle}
                suffix={publicDotcom}
                copied={copiedLink === 'public'}
                onCopy={() => void copyUrl(publicShareUrl, 'public')}
                onShare={() => void nativeShareUrl(publicShareUrl, 'public')}
                copyLabel={t('cv.share.copy')}
                shareLabel={t('cv.share.shareVia')}
              />
              {/* The public URL does not resolve while the profile is private.
                  Keep the corrective action beside the URL and open Access
                  without leaving the Share dialog. */}
              {privacy.profileVisibility !== 'public' ? (
                <button
                  type="button"
                  onClick={() => {
                    setAccessReason('link');
                    setView('access');
                  }}
                  style={privateNotice}
                >
                  <span style={privateNoticeIcon} aria-hidden>
                    <HugeiconsIcon icon={LockIcon} size={15} strokeWidth={2} />
                  </span>
                  <span style={privateNoticeText}>
                    {t('cv.share.profilePrivateNotice')}
                  </span>
                  <MaterialSymbol
                    name="chevron_right"
                    size={16}
                    style={{ color: 'var(--text-tertiary)', transform: isRTL ? 'scaleX(-1)' : undefined }}
                  />
                </button>
              ) : null}
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <ShareRow
              icon={<FilePhoneIcon size={22} strokeWidth={1.7} />}
              label={t('cv.share.contact.title')}
              chevron={chevronName}
              onClick={() => setView('contact')}
            />
            <ShareRow
              icon={<QrCode size={22} strokeWidth={1.7} />}
              label={t('cv.share.qr')}
              chevron={chevronName}
              onClick={openQr}
            />
            <ShareRow
              icon={<LinkedInIcon size={22} />}
              label={t('cv.share.addToLinkedin')}
              chevron={chevronName}
              onClick={() => openBioGuide('linkedin')}
              disabled={!publicShareUrl}
            />
            {canDownload ? (
              <ShareRow
                icon={<FileDown size={22} strokeWidth={1.7} />}
                label={t('cv.share.downloadCv')}
                chevron={chevronName}
                onClick={() => {
                  setDownloadError(null);
                  setView('download');
                }}
              />
            ) : null}
            <ShareRow
              icon={<ShieldCheck size={22} strokeWidth={1.7} />}
              label={t('cv.share.privacyControls')}
              chevron={chevronName}
              onClick={() => {
                setAccessReason(null);
                setView('access');
              }}
            />
          </div>
          {downloadError ? <p style={errorText}>{downloadError}</p> : null}
        </div>
      ) : null}

      {view === 'contact' ? (
        <div style={{ ...stack, gap: 14 }}>
          {isLoadingExistingLink ? (
            <div style={contactLoading} role="status">
              <MaterialSymbol name="progress_activity" size={22} />
            </div>
          ) : privateUrl ? (
            <>
              <p style={bodyText}>{t('cv.share.contact.readyIntro')}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={fieldLabel}>{t('cv.share.contact.linkLabel')}</span>
                <LinkPill
                  handle={privateHandle}
                  suffix={privateDotcom}
                  copied={copiedLink === 'private'}
                  onCopy={() => void copyUrl(privateUrl, 'private')}
                  onShare={() => void nativeShareUrl(privateUrl, 'private')}
                  copyLabel={t('cv.share.copy')}
                  shareLabel={t('cv.share.shareVia')}
                />
              </div>
              {/* The token stayed valid but still serves the previous
                  visibility. Say so instead of implying contacts are on. */}
              {visibilitySyncError ? (
                <button
                  type="button"
                  onClick={() => void retryPrivateVisibility().catch(() => {})}
                  disabled={isSyncingPrivateVisibility}
                  style={syncErrorRow}
                >
                  <MaterialSymbol name="error" size={14} />
                  <span style={{ flex: 1, textAlign: 'start' }}>
                    {t('cv.share.linkUpdateFailed')}
                  </span>
                  <span style={syncErrorRetry}>{t('cv.share.retry')}</span>
                </button>
              ) : null}
              {/* What the link shares and under which policy (E03). */}
              <ShareLinkDetails link={privateLink} cv={linkCv} />
              <p style={bodyText}>{t('cv.share.contact.readyHint')}</p>
              <button
                type="button"
                onClick={revokeContactLink}
                disabled={revoking}
                style={{ ...revokeBtn, opacity: revoking ? 0.6 : 1 }}
              >
                {revoking ? (
                  <MaterialSymbol name="progress_activity" size={18} />
                ) : (
                  t('cv.share.contact.revoke')
                )}
              </button>
            </>
          ) : (
            <>
              {contactLinkRevoked ? (
                <div style={revokedCard}>
                  <span style={{ color: colors.brandViolet, display: 'inline-flex', flexShrink: 0 }}>
                    <CircleCheck size={20} strokeWidth={1.8} />
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>
                      {t('cv.share.contact.revokedTitle')}
                    </span>
                    <span style={bodyText}>{t('cv.share.contact.revokedBody')}</span>
                  </span>
                </div>
              ) : null}
              <p style={bodyText}>{t('cv.share.contact.intro')}</p>
              <p style={bodyText}>{t('cv.share.contact.forwardHint')}</p>
              <button
                type="button"
                onClick={createContactLink}
                disabled={isCreatingPrivateUrl}
                style={{ ...blackBtn, height: 52, opacity: isCreatingPrivateUrl ? 0.7 : 1 }}
              >
                {isCreatingPrivateUrl ? (
                  <MaterialSymbol name="progress_activity" size={18} />
                ) : contactLinkRevoked ? (
                  t('cv.share.contact.createNew')
                ) : (
                  t('cv.share.contact.create')
                )}
              </button>
            </>
          )}
          <div style={divider} />
          <button
            type="button"
            onClick={() => void openMarketingLink('privacy', locale)}
            style={policyLink}
          >
            {t('cv.share.privacyPolicy')}
          </button>
        </div>
      ) : null}

      {view === 'download' ? (
        <div style={{ ...stack, gap: 4 }}>
          {/* Phone & email in the file only; the contact link is unaffected. */}
          <div style={{ ...formatMenuRow, cursor: 'default', borderBottom: '1px solid var(--hairline, rgba(0,0,0,0.06))', marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{t('cv.share.includeContact')}</span>
            <Toggle value={includeContact} onChange={setIncludeContact} />
          </div>
          {DOWNLOAD_FORMATS.map((fmt) => {
            const locked = exportAccess.isFormatLocked(fmt);
            const active = format === fmt;
            return (
              <button
                key={fmt}
                type="button"
                onClick={() => void download(fmt)}
                disabled={downloading}
                style={{ ...formatMenuRow, minHeight: 52 }}
              >
                <span style={{ fontSize: 16, fontWeight: 600 }}>{fmt.toUpperCase()}</span>
                {locked ? (
                  <span style={proBadge}>
                    <ProIcon size={12} color="currentColor" />
                    {exportAccess.lockLabel}
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{t('cv.share.free')}</span>
                    {downloading && active ? (
                      <MaterialSymbol name="progress_activity" size={18} />
                    ) : active ? (
                      <MaterialSymbol name="check" size={18} />
                    ) : null}
                  </span>
                )}
              </button>
            );
          })}
          {downloadError ? <p style={errorText}>{downloadError}</p> : null}
        </div>
      ) : null}

      {view === 'qr' && publicShareUrl ? (
        <div style={{ ...stack, alignItems: 'center' }}>
          <div ref={qrWrapRef} style={qrFrame}>
            <QRCodeCanvas
              value={withCvShareRef(publicShareUrl, 'qr')}
              size={208}
              bgColor="#ffffff"
              fgColor="#101013"
              level="H"
              imageSettings={
                hideQrLogo
                  ? undefined
                  : {
                      src: QR_LOGO_SVG,
                      height: 36,
                      width: 33,
                      excavate: true,
                    }
              }
            />
          </div>
          <span style={{ ...linkText, ...qrLinkText }}>
            <span style={{ color: 'var(--text-primary)' }}>{publicHandle}</span>
            <span style={{ color: 'var(--text-tertiary)' }}>{publicDotcom}</span>
          </span>
          <div style={{ ...hideRow, alignSelf: 'stretch' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{t('cv.share.hideLogo')}</span>
            {canBrand ? (
              <Toggle value={card.hideBranding} onChange={(next) => updateCVConfig({ card: { ...card, hideBranding: next } })} />
            ) : (
              <button type="button" onClick={() => upgrade.open('individual')} style={upgradeBadge}>
                {t('cv.customizeCard.upgradeBadge')}
              </button>
            )}
          </div>
          <button type="button" onClick={saveQr} style={ghostBtn}>
            <MaterialSymbol name="qr_code_2" size={16} />
            {t('cv.share.qrSave')}
          </button>
        </div>
      ) : null}

      {view === 'customize' ? (
        <CustomizeCardSheet publicUrl={publicShareUrl} onDone={() => setView('main')} />
      ) : null}

      {view === 'access' ? (
        <AccessView
          target={target ?? 'master'}
          notice={
            accessReason === null
              ? null
              : accessReason === 'link'
                ? t('cv.share.linkPublicRequired')
                : t('cv.share.platformPublicRequired', {
                    platform: tCommon(
                      getLinkInBioPlatform(accessReason)?.labelKey ??
                        'linkInBio.sectionTitle',
                    ),
                  })
          }
          onDone={() => {
            setAccessReason(null);
            setView('main');
          }}
        />
      ) : null}

      {bioPlatform && publicShareUrl ? (
        <AddToBioGuide
          platform={bioPlatform}
          url={publicShareUrl}
          copied={copiedLink === 'public'}
          onCopy={() => void copyUrl(publicShareUrl, 'public', bioPlatform.id)}
          // We know this user's actual LinkedIn profile, which beats sending
          // them to the generic /in/me/ redirect.
          webUrlOverride={
            bioPlatform.id === 'linkedin' ? linkedinProfileUrl : null
          }
          onPost={
            bioPlatform.shareIntent
              ? () => {
                  const intent = bioPlatform.shareIntent?.(
                    encodeURIComponent(withCvShareRef(publicShareUrl, bioPlatform.id)),
                  );
                  if (intent) window.open(intent, '_blank', 'noopener,noreferrer');
                }
              : undefined
          }
        />
      ) : null}
    </WebDialog>
  );
}

// ─── Access view ─────────────────────────────────────────────────────────

function AccessView({
  target,
  notice,
  onDone,
}: {
  target: string;
  notice?: string | null;
  onDone: () => void;
}) {
  const { t } = useTranslation('seeker');
  const { privacy, updatePrivacy } = useCVData();
  const options: { value: ProfileVisibility; symbol: string; title: string; hint: string }[] = [
    { value: 'public', symbol: 'public', title: t('cv.share.accessPublic'), hint: t('cv.share.accessPublicHint') },
    { value: 'employers', symbol: 'business_center', title: t('cv.share.accessEmployers'), hint: t('cv.share.accessEmployersHint') },
    { value: 'private', symbol: 'lock', title: t('cv.share.accessPrivate'), hint: t('cv.share.accessPrivateHint') },
  ];
  return (
    <div style={stack}>
      {notice ? <p style={accessNotice}>{notice}</p> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {options.map((opt) => {
          const active = privacy.profileVisibility === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => updatePrivacy({ ...privacy, profileVisibility: opt.value })}
              style={radioCard(active)}
            >
              <span style={radioIcon}>
                <MaterialSymbol name={opt.symbol} size={22} />
              </span>
              <span style={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600 }}>{opt.title}</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>{opt.hint}</span>
              </span>
              <span style={radioDot(active)}>{active ? <span style={radioDotFill} /> : null}</span>
            </button>
          );
        })}
      </div>
      <div style={divider} />
      <CvPeopleManager target={target as never} />
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-tertiary)' }}>{t('cv.share.inviteHint')}</p>
      <button type="button" onClick={onDone} style={blackBtn}>{t('cv.share.done')}</button>
    </div>
  );
}

// ─── LinkedIn view ───────────────────────────────────────────────────────

// ─── Atoms ───────────────────────────────────────────────────────────────

function LinkPill({
  handle,
  suffix,
  copied,
  onCopy,
  onShare,
  copyLabel,
  shareLabel,
}: {
  handle: string;
  suffix: string;
  copied: boolean;
  onCopy: () => void;
  onShare: () => void;
  copyLabel: string;
  shareLabel: string;
}) {
  return (
    <div style={linkPill}>
      <span style={linkText}>
        <span style={{ color: 'var(--text-primary)' }}>{handle}</span>
        <span style={{ color: 'var(--text-tertiary)' }}>{suffix}</span>
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button type="button" onClick={onCopy} aria-label={copyLabel} style={iconBtn}>
          <MaterialSymbol name={copied ? 'check' : 'content_copy'} size={19} />
        </button>
        <button type="button" onClick={onShare} aria-label={shareLabel} style={iconBtn}>
          <MaterialSymbol name="ios_share" size={19} />
        </button>
      </div>
    </div>
  );
}

function ShareRow({
  icon,
  label,
  chevron,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  chevron: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...shareRow,
        background: hover && !disabled ? 'var(--hover, #f3f2f8)' : 'transparent',
        opacity: disabled ? 0.35 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <span style={shareRowIcon}>{icon}</span>
      <span style={shareRowLabel}>{label}</span>
      <MaterialSymbol name={chevron} size={20} style={{ color: 'var(--text-tertiary)' }} />
    </button>
  );
}

function Toggle({
  value,
  loading,
  onChange,
}: {
  value: boolean;
  loading?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      disabled={loading}
      onClick={() => onChange(!value)}
      style={{
        position: 'relative',
        width: 58,
        height: 34,
        borderRadius: 9999,
        border: '1px solid var(--input-border)',
        cursor: loading ? 'default' : 'pointer',
        flexShrink: 0,
        // Mirrors the native fix (issue #226): ON = text-primary track with a
        // bg-coloured knob, OFF = a plain input-bg track with a tertiary knob.
        // The track used to be a hardcoded rgba(255,255,255,0.64) in BOTH states,
        // which in dark mode composited to ~#aeaeae under a #e3e3e3 knob: ~1.7:1,
        // i.e. an invisible ON knob. Theme vars keep both themes legible.
        background: value ? 'var(--text-primary)' : 'var(--input-bg)',
        boxShadow: 'var(--gem-input-shadow)',
        transition: 'background-color 150ms',
      }}
    >
      {loading ? (
        <span style={toggleLoader}>
          <MaterialSymbol name="progress_activity" size={15} />
        </span>
      ) : null}
      <span
        style={{
          position: 'absolute',
          top: 4,
          insetInlineStart: value ? 28 : 4,
          width: 24,
          height: 24,
          borderRadius: 12,
          background: value ? 'var(--bg)' : 'var(--text-tertiary)',
          opacity: loading ? 0.28 : 1,
          transition: 'inset-inline-start 150ms, background-color 150ms, opacity 150ms',
        }}
      />
    </button>
  );
}

const linkPill: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 52,
  border: '1px solid var(--input-border)',
  borderRadius: 16,
  paddingInline: '16px 8px',
};
const shareRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  width: '100%',
  minHeight: 52,
  padding: '0 4px',
  border: 0,
  borderRadius: 12,
  background: 'transparent',
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  textAlign: 'start',
};
const shareRowIcon: CSSProperties = {
  width: 24,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: 'var(--text-primary)',
};
const shareRowLabel: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 16,
  fontWeight: 500,
  letterSpacing: -0.16,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const contactLoading: CSSProperties = {
  minHeight: 120,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-tertiary)',
};
const bodyText: CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: '20px',
  color: 'var(--text-secondary)',
};
const fieldLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text-tertiary)',
};
const revokeBtn: CSSProperties = {
  height: 52,
  borderRadius: 9999,
  border: '1px solid rgba(217,48,37,0.14)',
  background: 'rgba(217,48,37,0.05)',
  color: 'var(--danger, #d93025)',
  cursor: 'pointer',
  fontSize: 16,
  fontWeight: 500,
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const revokedCard: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  border: '1px solid var(--input-border)',
  borderRadius: 16,
  padding: 14,
};
const policyLink: CSSProperties = {
  alignSelf: 'center',
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
  fontSize: 13,
  textDecoration: 'underline',
  fontFamily: 'inherit',
  padding: 2,
};
const stack: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  fontFamily: "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
  color: 'var(--text-primary)',
};
const linkText: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 16,
  fontWeight: 500,
};
const qrLinkText: CSSProperties = {
  alignSelf: 'stretch',
  maxWidth: '100%',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  textAlign: 'center',
  lineHeight: 1.35,
};
const iconBtn: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  border: 0,
  cursor: 'pointer',
  background: 'transparent',
  color: 'var(--text-primary)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const divider: CSSProperties = { height: 1, background: 'var(--hairline, rgba(0,0,0,0.06))' };
const accessNotice: CSSProperties = {
  margin: 0,
  padding: '10px 12px',
  borderRadius: 12,
  background: 'var(--surface-secondary, var(--tabbio-surface-secondary, rgba(0,0,0,0.04)))',
  color: 'var(--text-secondary)',
  fontSize: 13,
  lineHeight: '18px',
};
const toggleLoader: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-secondary)',
  zIndex: 2,
};
const syncErrorRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  border: 0,
  borderRadius: 12,
  padding: '8px 10px',
  cursor: 'pointer',
  background: 'var(--danger-soft, rgba(220,38,38,0.08))',
  color: 'var(--danger, #dc2626)',
  fontSize: 12,
  fontWeight: 500,
};
const syncErrorRetry: CSSProperties = { fontWeight: 600, textDecoration: 'underline' };
const privateNotice: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  marginTop: 10,
  padding: '8px 9px',
  border: 0,
  borderRadius: 10,
  background: 'rgba(0,0,0,0.04)',
  cursor: 'pointer',
  color: colors.brandVioletLight,
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 500,
  fontFamily: 'inherit',
  textAlign: 'start',
};
const privateNoticeIcon: CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 13,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: 'var(--input-bg)',
  color: colors.brandVioletLight,
};
const privateNoticeText: CSSProperties = {
  flex: 1,
};
const errorText: CSSProperties = {
  margin: '8px 4px 0',
  fontSize: 12,
  lineHeight: '16px',
  color: '#b42318',
};
const formatMenuRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  minHeight: 44,
  padding: '0 8px',
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};
const proBadge: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: 'rgba(90,42,255,0.10)',
  color: colors.brandViolet,
  borderRadius: 20,
  padding: '5px 10px',
  fontSize: 11,
  fontWeight: 700,
};
const qrFrame: CSSProperties = { padding: 22, borderRadius: 24, background: '#ffffff', border: '1px solid var(--input-border)' };
const hideRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  borderRadius: 16,
  padding: 16,
  background: 'var(--hover, rgba(0,0,0,0.04))',
  justifyContent: 'space-between',
};
const ghostBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  minHeight: 44,
  width: '100%',
  borderRadius: 9999,
  border: 0,
  background: 'transparent',
  color: 'var(--text-secondary)',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
const upgradeBadge: CSSProperties = {
  border: 0,
  cursor: 'pointer',
  background: colors.brandViolet,
  color: '#fff',
  borderRadius: 20,
  padding: '4px 8px',
  fontSize: 10,
  fontWeight: 700,
  fontFamily: 'inherit',
};
const blackBtn: CSSProperties = {
  height: 56,
  borderRadius: 9999,
  border: 0,
  cursor: 'pointer',
  background: 'var(--text-primary)',
  color: 'var(--bg)',
  fontSize: 16,
  fontWeight: 600,
  fontFamily: 'inherit',
};
const radioCard = (active: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  borderRadius: 16,
  border: `1px solid ${active ? colors.brandViolet : 'var(--input-border)'}`,
  background: 'transparent',
  padding: 14,
  cursor: 'pointer',
  fontFamily: 'inherit',
  color: 'var(--text-primary)',
});
const radioIcon: CSSProperties = {
  width: 32,
  height: 32,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
const radioDot = (active: boolean): CSSProperties => ({
  width: 20,
  height: 20,
  borderRadius: 10,
  border: `1px solid ${active ? colors.brandViolet : 'var(--input-border)'}`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});
const radioDotFill: CSSProperties = { width: 10, height: 10, borderRadius: 5, background: colors.brandViolet };

const QR_LOGO_SVG = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24.0361 27.9998"><path d="${TABBIO_MARK_PATH}" fill="black"/></svg>`,
)}`;
